import { useCallback, useSyncExternalStore } from 'react';

import {
  IsLauncherOpen,
  LauncherCloseAttempts,
  NextLauncherOpenAt,
} from '@lib/core/service/utils/widgetLauncherSessionState';

export type TSetIsLauncherOpen = boolean | ((prev: boolean) => boolean);

/**
 * Widget launcher state backed by sessionStorage.
 *
 * The embed may reload the iframe on another host page, so launcher open/closed state
 * (expanded vs collapsed), close attempts, and the reopen timer must survive in sessionStorage.
 *
 * sessionStorage is the single source of truth: useSyncExternalStore keeps every hook
 * instance in sync, including writes from outside React (comms VH_WIDGET_CLOSED / LAUNCHER_INITIAL_CLOSED handlers).
 */
export const useWidgetLauncherSessionState = () => {
  const isLauncherOpen = useSyncExternalStore(IsLauncherOpen.subscribe, IsLauncherOpen.get);
  const remainingLauncherCloseAttempts = useSyncExternalStore(
    LauncherCloseAttempts.subscribe,
    LauncherCloseAttempts.get,
  );
  const nextLauncherOpenAt = useSyncExternalStore(NextLauncherOpenAt.subscribe, NextLauncherOpenAt.get);

  const setIsLauncherOpen = useCallback((value: TSetIsLauncherOpen) => {
    const next = typeof value === 'function' ? value(IsLauncherOpen.get()) : value;

    IsLauncherOpen.set(next);
  }, []);

  const clearNextLauncherOpenAt = useCallback(() => {
    NextLauncherOpenAt.clear();
  }, []);

  // Single shared timestamp for the next automatic launcher open (manual close, product interaction, or persisted delay).
  const setNextLauncherOpenAt = useCallback((timestamp: number) => {
    NextLauncherOpenAt.set(timestamp);
  }, []);

  const setRemainingLauncherCloseAttempts = useCallback((next: number) => {
    LauncherCloseAttempts.set(next);
  }, []);

  // Used when the user opens the fullscreen widget or an external trigger should ignore past manual closes.
  const resetLauncherCloseAttempts = useCallback(() => {
    NextLauncherOpenAt.clear();
  }, []);

  return {
    clearNextLauncherOpenAt,
    isLauncherOpen,
    nextLauncherOpenAt,
    remainingLauncherCloseAttempts,
    resetLauncherCloseAttempts,
    setIsLauncherOpen,
    setNextLauncherOpenAt,
    setRemainingLauncherCloseAttempts,
  };
};
